import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useWaitlist } from './MarketingLayout';
import { useMarketingMeta } from './useMarketingMeta';

const PHONE_CATEGORIES = ['Canteen', 'Coaching', 'Grounds', 'Events'] as const;

const PHONE_ROLES = [
  { role: 'Canteen helper', club: 'Local surf club', when: 'Sat mornings', hours: '3 hrs/wk' },
  { role: 'Junior coach assistant', club: 'Community footy club', when: 'Weeknights', hours: '1 to 2 hrs/wk' },
  { role: 'Line marking', club: 'Local cricket club', when: 'Sunday', hours: '2 hrs/wk' },
  { role: 'Event setup', club: 'Community netball club', when: 'Match days', hours: '2 hrs/wk' },
];

export default function HomePage() {
  const { openWaitlist, prefersReducedMotion } = useWaitlist();
  const rolesListRef = useRef<HTMLDivElement | null>(null);

  useMarketingMeta({
    title: 'VolunteerCo · Australia-wide volunteering made clearer',
    description:
      'VolunteerCo helps clubs and community groups across Australia set up ongoing volunteer roles with clear expectations and hours signed off. Free for clubs.',
  });

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    const el = rolesListRef.current;
    if (!el) return undefined;

    let raf = 0;
    let direction = 1;
    let paused = false;

    const tick = () => {
      if (!paused && el.scrollHeight > el.clientHeight + 4) {
        el.scrollTop += direction * 0.35;
        const maxScroll = el.scrollHeight - el.clientHeight;
        if (el.scrollTop >= maxScroll - 1) direction = -1;
        if (el.scrollTop <= 0) direction = 1;
      }
      raf = window.requestAnimationFrame(tick);
    };

    raf = window.requestAnimationFrame(tick);

    const pause = () => {
      paused = true;
    };
    const resume = () => {
      paused = false;
    };

    el.addEventListener('pointerenter', pause);
    el.addEventListener('pointerleave', resume);
    el.addEventListener('focusin', pause);
    el.addEventListener('focusout', resume);

    return () => {
      window.cancelAnimationFrame(raf);
      el.removeEventListener('pointerenter', pause);
      el.removeEventListener('pointerleave', resume);
      el.removeEventListener('focusin', pause);
      el.removeEventListener('focusout', resume);
    };
  }, [prefersReducedMotion]);

  return (
    <>
      <section className="oct16-hero" id="top" aria-labelledby="hero-title" data-hero-parallax>
        <div className="oct16-container oct16-hero-grid">
          <div className="oct16-hero-copy">
            <p className="oct16-eyebrow" data-reveal>
              Across Australia
            </p>
            <h1 id="hero-title" className="oct16-hero-title" data-reveal>
              <span>Volunteering is broken.</span>
              <span className="oct16-blue-line">
                We’re fixing it.
                <span className="oct16-brush" aria-hidden="true" />
              </span>
            </h1>
            <p className="oct16-hero-subtitle" data-reveal>
              VolunteerCo is building a clearer way to join ongoing roles with clubs and community groups across
              Australia.
            </p>

            <div className="oct16-hero-actions" data-reveal>
              <button type="button" className="oct16-primary" onClick={() => openWaitlist({ persona: 'volunteer' })}>
                Get started
              </button>
              <a className="oct16-secondary" href="#impact">
                See how it works
              </a>
            </div>

            <div className="oct16-truths" aria-label="What VolunteerCo does" data-reveal>
              <div className="oct16-truth">
                <div className="oct16-truth-label">Free for clubs</div>
              </div>
              <div className="oct16-truth">
                <div className="oct16-truth-label">Clear roles</div>
              </div>
              <div className="oct16-truth">
                <div className="oct16-truth-label">Hours signed off</div>
              </div>
            </div>

          </div>

          <div className="oct16-hero-media" aria-hidden="true">
            <picture className="oct16-photo">
              <source
                type="image/avif"
                srcSet="/landing/oct16/hero-volunteers-800.avif 800w, /landing/oct16/hero-volunteers-1600.avif 1600w, /landing/oct16/hero-volunteers-2400.avif 2400w"
              />
              <source srcSet="/landing/oct16/hero-volunteers-800.webp 800w, /landing/oct16/hero-volunteers-1600.webp 1600w, /landing/oct16/hero-volunteers-2400.webp 2400w" />
              <img
                src="/landing/oct16/hero-volunteers-1600.webp"
                alt=""
                width={1600}
                height={2000}
                loading="eager"
                decoding="async"
                fetchPriority="high"
                sizes="(max-width: 960px) 92vw, 44vw"
              />
            </picture>

            <div className="oct16-note" aria-hidden="true">
              <span className="oct16-note-text">Real people. Brighter places.</span>
            </div>
          </div>
        </div>
      </section>

      <section className="oct16-split" id="impact" aria-labelledby="impact-title">
        <div className="oct16-container oct16-split-grid">
          <div className="oct16-split-left">
            <p className="oct16-eyebrow" data-reveal>
              A movement, not a platform
            </p>
            <h2 id="impact-title" className="oct16-section-title" data-reveal>
              <span>No more guilt.</span>
              <span className="oct16-blue-line">
                Just action.
                <span className="oct16-brush" aria-hidden="true" />
              </span>
            </h2>
            <p className="oct16-body" data-reveal>
              Clubs don’t need more sign-ups. They need volunteers who know what to do, can show up each week, and can
              have their hours signed off.
            </p>

            <div className="oct16-split-cta" data-reveal>
              <button type="button" className="oct16-primary" onClick={() => openWaitlist({ persona: 'volunteer' })}>
                Join the waitlist
              </button>
            </div>
          </div>

          <div className="oct16-phone-wrap" aria-label="Illustrative app preview" data-reveal>
            <span className="oct16-phone-illustrative">Illustrative</span>
            <div className="oct16-phone">
              <div className="oct16-phone-status" aria-hidden="true">
                <span className="oct16-phone-time">9:41</span>
                <span className="oct16-phone-notch" />
                <span className="oct16-phone-status-icons" />
              </div>
              <div className="oct16-phone-screen" role="img" aria-label="Illustrative role picker concept">
                <div className="oct16-phone-chips" aria-hidden="true">
                  {PHONE_CATEGORIES.map((label) => (
                    <span
                      key={label}
                      className={`oct16-phone-chip${label === 'Canteen' ? ' is-selected' : ''}`}
                    >
                      {label}
                    </span>
                  ))}
                </div>
                <div className="oct16-phone-roles" ref={rolesListRef} tabIndex={-1}>
                  {PHONE_ROLES.map((item) => (
                    <article className="oct16-role-card oct16-role-card--compact" key={item.role}>
                      <div className="oct16-role-top">
                        <div>
                          <div className="oct16-role-title">{item.role}</div>
                          <div className="oct16-role-club">{item.club}</div>
                        </div>
                      </div>
                      <div className="oct16-role-meta">
                        <div>
                          <div className="oct16-role-meta-k">When</div>
                          <div className="oct16-role-meta-v">{item.when}</div>
                        </div>
                        <div>
                          <div className="oct16-role-meta-k">Hours</div>
                          <div className="oct16-role-meta-v">{item.hours}</div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="oct16-role-cta"
                        onClick={() => openWaitlist({ persona: 'volunteer' })}
                      >
                        Express interest
                      </button>
                    </article>
                  ))}
                </div>
              </div>
            </div>

            <div className="oct16-side-label" aria-hidden="true">
              <span className="oct16-side-rule" />
              <span className="oct16-side-text">People · clubs · communities</span>
            </div>
          </div>
        </div>
      </section>

      <section className="oct16-sections" aria-label="Who VolunteerCo is for">
        <div className="oct16-container">
          <div className="oct16-two-col" aria-labelledby="volunteers-title">
            <div className="oct16-two-col-copy">
              <p className="oct16-label" data-reveal>
                For Volunteers
              </p>
              <h2 id="volunteers-title" className="oct16-section-title" data-reveal>
                Show up once.<span className="oct16-blue-inline"> Keep it going.</span>
              </h2>
              <p className="oct16-body" data-reveal>
                A clearer path into ongoing roles: what it involves, when it’s on, and how hours get signed off.
              </p>
              <Link className="oct16-primary" to="/volunteers" data-reveal>
                Learn more
              </Link>
            </div>
            <picture className="oct16-photo oct16-photo-wide">
              <source
                type="image/avif"
                srcSet="/landing/oct16/volunteers-800.avif 800w, /landing/oct16/volunteers-1600.avif 1600w, /landing/oct16/volunteers-2400.avif 2400w"
              />
              <source srcSet="/landing/oct16/volunteers-800.webp 800w, /landing/oct16/volunteers-1600.webp 1600w, /landing/oct16/volunteers-2400.webp 2400w" />
              <img
                src="/landing/oct16/volunteers-1600.webp"
                alt=""
                loading="lazy"
                decoding="async"
                sizes="(max-width: 960px) 92vw, 52vw"
              />
            </picture>
          </div>

          <div className="oct16-two-col" aria-labelledby="clubs-title">
            <picture className="oct16-photo oct16-photo-wide">
              <source
                type="image/avif"
                srcSet="/landing/oct16/clubs-800.avif 800w, /landing/oct16/clubs-1600.avif 1600w, /landing/oct16/clubs-2400.avif 2400w"
              />
              <source srcSet="/landing/oct16/clubs-800.webp 800w, /landing/oct16/clubs-1600.webp 1600w, /landing/oct16/clubs-2400.webp 2400w" />
              <img
                src="/landing/oct16/clubs-1600.webp"
                alt=""
                loading="lazy"
                decoding="async"
                sizes="(max-width: 960px) 92vw, 52vw"
              />
            </picture>
            <div className="oct16-two-col-copy">
              <p className="oct16-label" data-reveal>
                For Organisations
              </p>
              <h2 id="clubs-title" className="oct16-section-title" data-reveal>
                Set up the role.<span className="oct16-blue-inline"> Keep the help.</span>
              </h2>
              <p className="oct16-body" data-reveal>
                We help clubs and community groups set up ongoing roles that people can actually commit to.
              </p>
              <Link className="oct16-primary" to="/organisations" data-reveal>
                Learn more
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="oct16-close" aria-labelledby="close-title">
        <div className="oct16-close-media" aria-hidden="true">
          <picture>
            <source
              type="image/avif"
              srcSet="/landing/oct16/closing-800.avif 800w, /landing/oct16/closing-1600.avif 1600w, /landing/oct16/closing-2400.avif 2400w"
            />
            <source srcSet="/landing/oct16/closing-800.webp 800w, /landing/oct16/closing-1600.webp 1600w, /landing/oct16/closing-2400.webp 2400w" />
            <img src="/landing/oct16/closing-1600.webp" alt="" loading="lazy" decoding="async" sizes="100vw" />
          </picture>
          <div className="oct16-close-scrim" />
        </div>

        <div className="oct16-container oct16-close-inner">
          <h2 id="close-title" className="oct16-close-title" data-reveal>
            A more active, more human world
            <span className="oct16-blue-inline"> is possible.</span>
          </h2>
          <button type="button" className="oct16-pill-cta" onClick={() => openWaitlist({ persona: 'volunteer' })} data-reveal>
            Join the waitlist
          </button>
        </div>
      </section>

      <section className="lp-founder" id="founder" aria-labelledby="founder-title">
        <div className="lp-container">
          <h2 id="founder-title" className="lp-section-title" data-reveal>
            Who’s behind VolunteerCo
          </h2>
          <div className="lp-founder-grid" data-reveal>
            <div className="lp-founder-avatar">
              <img
                className="lp-founder-photo"
                src="/landing/oct16/silas-portrait-400.webp"
                srcSet="/landing/oct16/silas-portrait-400.webp 1x, /landing/oct16/silas-portrait-800.webp 2x"
                width={200}
                height={200}
                alt="Silas"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="lp-founder-copy">
              <p>
                Hey, I'm Silas. I live around Palm Beach on the Gold Coast. My background is in cybersecurity and tech consulting, and these days I build products.
              </p>
              <p>
                Sport has been a big part of my life. I've played in, captained and helped run all sorts of local clubs over the years, and they've given me some of my best mates and a real sense of belonging. Community sport is one of the best things we have going.
              </p>
              <p>
                But I kept seeing the same thing. At my soccer club there were 100+ people around every week, and only a handful doing the work that kept it running. It wasn't that people didn't care. Most were never asked, or didn't know what helping would actually involve.
              </p>
              <p>
                That's why I started VolunteerCo: to make it easy to say yes. Clear roles, a club that's ready for you, and hours that actually get recognised. We're starting small and doing it properly.
              </p>
              <p>
                If you'd like a chat, email <a href="mailto:silas@volunteer-co.com">silas@volunteer-co.com</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

    </>
  );
}

